import { FAIcon } from '@/components'
import { useWorkshopMutations } from '@/modules/vehicles/fleet/mutations.hooks'
import type { WorkshopNode } from '@/modules/vehicles/types.graphql'
import { useConfirm, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { WorkshopForm } from './workshop-form'
import { WorkshopPrompt } from './workshop-prompt'

interface WorkshopRowActionsProps {
  workshop: WorkshopNode
}

export const WorkshopRowActions: React.FC<WorkshopRowActionsProps> = ({
  workshop,
}) => {
  const translations = useTranslate({
    prompt: {
      delete: ['settings.prompts.workshop-delete', { name: '' }],
      deleteInUse: [
        'settings.prompts.workshop-delete-in-use',
        { name: '', n: 0 },
      ],
      deleteTitle: 'settings.prompts.workshop-delete-title',
    },
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const { patchWorkshop, deleteWorkshop } = useWorkshopMutations()

  const { hasPermissions } = usePermissions()
  const canChange = hasPermissions(
    PERMISSIONS.vehicles.change.vehicleexpiration,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicleexpiration,
    true
  )

  async function handleEdit() {
    const { data } = await addPrompt<WorkshopForm | null>(resolve => (
      <WorkshopPrompt workshop={workshop} onSubmit={resolve} />
    ))
    if (!data) return

    await patchWorkshop({ variables: { id: workshop.id, input: data } })
  }

  async function handleDelete() {
    const { data: answer } = await confirm(
      workshop.servicePlansCount > 0
        ? translations.prompt.deleteInUse({
            name: workshop.name,
            n: workshop.servicePlansCount,
          })
        : translations.prompt.delete({ name: workshop.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    await deleteWorkshop({ variables: { id: workshop.id } })
  }

  if (!canChange && !canDelete) return null

  return (
    <span className="flex items-center justify-end gap-4">
      {canChange && (
        <FAIcon
          className="text-gray-400 hover:text-secondary"
          icon="edit"
          cursor="pointer"
          onClick={() => void handleEdit()}
        />
      )}
      {canDelete && (
        <FAIcon
          className="text-gray-400 hover:text-red-500"
          icon="times"
          cursor="pointer"
          onClick={() => void handleDelete()}
        />
      )}
    </span>
  )
}
